import { useSite, useContent } from "../context/SiteContext";
import { mediaUrl } from "../lib/api";
import { Container, PageHero } from "../components/site/Common";
import { CTA, Affiliations } from "./Home";
import { CheckCircle2, FileText, Download, UserRound } from "lucide-react";

const Leadership = () => {
  const { items: people } = useContent("leaders");
  if (!people?.length) return null;
  return (
    <div className="mt-16" data-testid="about-leadership">
      <p className="eyebrow mb-3">Leadership</p>
      <h2 className="font-display text-3xl font-bold text-[#0B1F44] mb-8">Our Leadership</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {people.map((p) => (
          <article key={p.id} className="card overflow-hidden text-center" data-testid={`leadership-card-${p.id}`}>
            <div className="aspect-[4/5] bg-gradient-to-b from-slate-100 to-slate-200 overflow-hidden">
              {p.photo ? <img src={mediaUrl(p.photo)} alt={p.name} className="h-full w-full object-cover object-top" data-testid={`leadership-photo-${p.id}`} /> : <div className="h-full grid place-items-center text-slate-400" data-testid={`leadership-placeholder-${p.id}`}><UserRound className="h-20 w-20" /></div>}
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-semibold text-[#0B1F44]" data-testid={`leadership-name-${p.id}`}>{p.name}</h3>
              <p className="text-sm font-semibold text-blue-700" data-testid={`leadership-designation-${p.id}`}>{p.designation}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const Block = ({ title, text, testid }) => !text ? null : (
  <div className="card p-8" data-testid={testid}>
    <h3 className="font-display text-xl font-semibold text-[#0B1F44]">{title}</h3>
    <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line">{text}</p>
  </div>
);

const AboutSections = () => {
  const { items } = useContent("about_sections");
  if (!items?.length) return null;
  return (
    <div className="mt-16 space-y-10" data-testid="about-custom-sections">
      {items.map((sec, i) => (
        <div key={sec.id} className={`grid gap-8 items-center ${sec.image ? "lg:grid-cols-2" : ""}`} data-testid={`about-section-${sec.id}`}>
          {sec.image && <img src={mediaUrl(sec.image)} alt={sec.title} className={`w-full aspect-[16/10] object-cover rounded-2xl ${i % 2 ? "lg:order-2" : ""}`} />}
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0B1F44]">{sec.title}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-line">{sec.content}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const Documents = () => {
  const { items } = useContent("documents");
  if (!items?.length) return null;
  return (
    <div className="mt-16" data-testid="about-documents">
      <h2 className="font-display text-2xl font-bold text-[#0B1F44] mb-6">Documents & downloads</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((d) => (
          <a key={d.id} href={mediaUrl(d.file)} target="_blank" rel="noreferrer" className="card p-5 flex items-center gap-4" data-testid={`document-${d.id}`}>
            <span className="h-11 w-11 rounded-xl bg-red-50 text-red-600 grid place-items-center"><FileText className="h-5 w-5" /></span>
            <span className="flex-1"><span className="block font-semibold text-[#0B1F44]">{d.title}</span><span className="block text-sm text-slate-500">{d.description}</span></span>
            <Download className="h-4 w-4 text-slate-400" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default function About() {
  const { settings: s } = useSite();
  if (!s) return <div className="min-h-screen" />;
  const objectives = (s.objectives || "").split("\n").filter(Boolean);
  const hasVerified = s.affiliations?.some((a) => a.verified);
  return (
    <>
      <PageHero eyebrow="About TDBA" title={s.association_name} text={s.about_intro} />
      <section className="py-20 bg-white">
        <Container>
          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <div>
              {s.history && (
                <>
                  <p className="eyebrow mb-3">Our history</p>
                  <h2 className="font-display text-3xl font-bold text-[#0B1F44]">Where we come from</h2>
                  <p className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="about-history">{s.history}</p>
                </>
              )}
              <div className={`${s.history ? "mt-8" : ""} grid grid-cols-2 gap-4`}>
                {s.established && <div className="rounded-2xl bg-slate-50 p-5"><p className="text-xs text-slate-500">Established</p><p className="font-display font-bold text-[#0B1F44] mt-1">{s.established}</p></div>}
                <div className="rounded-2xl bg-slate-50 p-5"><p className="text-xs text-slate-500">Location</p><p className="font-display font-bold text-[#0B1F44] mt-1">{s.location}</p></div>
              </div>
            </div>
            <div className="grid gap-6">
              <div className="rounded-2xl bg-[#0B1F44] text-white p-8" data-testid="about-mission"><p className="eyebrow !text-blue-300 mb-2">Mission</p><p className="text-slate-200 leading-relaxed">{s.mission}</p></div>
              <div className="rounded-2xl bg-blue-600 text-white p-8" data-testid="about-vision"><p className="eyebrow !text-blue-100 mb-2">Vision</p><p className="text-blue-50 leading-relaxed">{s.vision}</p></div>
            </div>
          </div>
          {objectives.length > 0 && <div className="mt-16">
            <h2 className="font-display text-2xl font-bold text-[#0B1F44] mb-6">Objectives</h2>
            <ul className="grid gap-4 md:grid-cols-2" data-testid="about-objectives">
              {objectives.map((o) => <li key={o} className="flex gap-3 rounded-2xl border border-slate-200 p-5"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /><span className="text-slate-700">{o}</span></li>)}
            </ul>
          </div>}
          <Leadership />
          <div className="mt-16 grid gap-6 md:grid-cols-2 empty:hidden">
            <Block title="Player development" text={s.development_activities} testid="about-player-development" />
            <Block title="Coaching programmes" text={s.coaching_programmes} testid="about-coaching" />
            <Block title="Competitive badminton" text={s.competitive_pathway} testid="about-competitive" />
            <Block title="Community activities" text={s.community_development} testid="about-community" />
          </div>
          <AboutSections />
          {!hasVerified && (
            <div className="mt-16 rounded-2xl border border-dashed border-slate-300 p-8 text-slate-500" data-testid="about-affiliations-pending">
              <h2 className="font-display text-xl font-bold text-[#0B1F44] mb-2">Official affiliations</h2>
              Affiliation details will be published here once officially verified.
            </div>
          )}
          <Documents />
        </Container>
      </section>
      {hasVerified && <Affiliations s={s} />}
      <CTA s={s} />
    </>
  );
}
